import { getUnknownSignalFnName } from './utils/debug-name.js';
import { getSignal } from './get.js';
import { cancelPull, schedulePull } from './scheduling.js';
import { hashValue } from './utils/hash.js';
import { stringifyValue } from './utils/stringify.js';
import { unwatchSignal, watchSignal } from './watch.js';
import { equalsFrom } from './utils/equals.js';
let ID = 0;
/**
 * Unified way to create a reactive definition (protects shaping)
 */
export function createReactiveDefinition(id, desc, compute, equals, isRelay, paramKey, tracer) {
    const def = {
        compute,
        equals: equalsFrom(equals),
        isRelay,
        paramKey,
        tracer: undefined,
    };
    if (IS_DEV) {
        def.id = id;
        def.desc = desc;
        def.tracer = tracer;
    }
    return def;
}
export class ReactiveSignal {
    // Bitmask containing state in the first 2 bits and boolean properties in the remaining bits
    flags;
    scope = undefined;
    id = ++ID;
    subs = new Map();
    deps = new Map();
    ref = new WeakRef(this);
    dirtyHead = undefined;
    updatedCount = 0;
    computedCount = 0;
    watchCount = 0;
    key;
    args;
    callbacks = undefined;
    _listeners = null;
    _value = undefined;
    tracerMeta;
    desc;
    // Reference to the shared definition
    def;
    constructor(def, args, key, scope) {
        this.flags = (def.isRelay ? 8 /* ReactiveFnFlags.isRelay */ : 0) | 2 /* ReactiveFnState.Dirty */;
        this.scope = scope;
        this.key = key;
        this.args = args;
        this.def = def;
        this.desc = def.desc;
        if (IS_DEV) {
            this.tracerMeta = {
                id: def.id ?? key ?? hashValue([def.compute, ID++]),
                desc: def.desc ?? def.compute.name ?? getUnknownSignalFnName(def.compute),
                params: args.map(arg => stringifyValue(arg)).join(', '),
                tracer: def.tracer,
            };
        }
    }
    get _state() {
        return this.flags & 7 /* ReactiveFnFlags.State */;
    }
    set _state(state) {
        this.flags = (this.flags & ~7 /* ReactiveFnFlags.State */) | state;
    }
    get _isListener() {
        return (this.flags & 16 /* ReactiveFnFlags.isListener */) !== 0;
    }
    get _isActive() {
        return (this.flags & 32 /* ReactiveFnFlags.isActive */) !== 0;
    }
    set _isActive(isActive) {
        if (isActive) {
            this.flags |= 32 /* ReactiveFnFlags.isActive */;
        }
        else {
            this.flags &= ~32 /* ReactiveFnFlags.isActive */;
        }
    }
    get _isLazy() {
        return (this.flags & 64 /* ReactiveFnFlags.isLazy */) !== 0;
    }
    set _isLazy(isLazy) {
        if (isLazy) {
            this.flags |= 64 /* ReactiveFnFlags.isLazy */;
        }
        else {
            this.flags &= ~64 /* ReactiveFnFlags.isLazy */;
        }
    }
    get listeners() {
        return (this._listeners ??
            (this._listeners = {
                updatedAt: 0,
                current: new Map(),
                cachedBoundAdd: this.addListener.bind(this),
            }));
    }
    get value() {
        return getSignal(this);
    }
    addListener(listener, opts) {
        const { current } = this.listeners;
        if (!current.has(listener)) {
            let effective = listener;
            if (opts?.skipInitial) {
                let initial = true;
                effective = () => {
                    if (initial) {
                        initial = false;
                        return;
                    }
                    listener();
                };
            }
            if (!this._isListener) {
                watchSignal(this);
                this.flags |= 16 /* ReactiveFnFlags.isListener */;
            }
            if (this.watchCount > 0) {
                schedulePull(this);
            }
            current.set(listener, effective);
        }
        return () => {
            if (current.has(listener)) {
                current.delete(listener);
                if (current.size === 0) {
                    cancelPull(this);
                    unwatchSignal(this);
                    this.flags &= ~16 /* ReactiveFnFlags.isListener */;
                    this.listeners.updatedAt = 0;
                }
            }
        };
    }
    // This method is used in React hooks specifically. It returns a bound add method
    // that is cached to avoid creating a new one on each call, and it eagerly sets
    // the listener as watched so that relays that are accessed will be activated.
    addListenerLazy(watch = true) {
        if (!this._isListener) {
            if (watch) {
                watchSignal(this);
            }
            this.flags |= 16 /* ReactiveFnFlags.isListener */;
        }
        return this.listeners.cachedBoundAdd;
    }
}
export const runListeners = (signal) => {
    const { listeners } = signal;
    if (listeners === null) {
        return;
    }
    const { current } = listeners;
    for (const listener of current.values()) {
        listener();
    }
};
export const isRelay = (signal) => {
    return (signal['flags'] & 8 /* ReactiveFnFlags.isRelay */) !== 0;
};
export function createReactiveSignal(def, args = [], key, scope) {
    return new ReactiveSignal(def, args, key, scope);
}
